import { BehaviorSubject } from 'rxjs';
import { User } from 'shared/models/site/user';
import { UserProfile } from 'shared/models/site/user-profile';
import { DocumentType } from "shared/models/site/document-type"

export enum SignedInState {
    Loading,
    NotSignedIn,
    SignedIn
}

export type ApplicationState = {
    user?: User,
    profile?: UserProfile,
    signedInState?: SignedInState,
    railDocumentTypes?: DocumentType[],
    searchableRailDocumentTypes?: DocumentType[],
    writableRailDocumentTypes?: DocumentType[],
    gnhFormsFeatureToggle: boolean,
    backOfHouseRoles: string[],
    isBackOfHouseUser: boolean,
};

export const initialState: ApplicationState = {
    backOfHouseRoles: ["BackOffice", "Supervisor", "SafeWorking"],
    isBackOfHouseUser: false,
    gnhFormsFeatureToggle: false
};

const documentStateSubject = new BehaviorSubject<ApplicationState>(initialState);

const setState = (updateFn: (appState: ApplicationState) => void) => {
    var state = Object.assign({}, documentStateSubject.getValue());;

    updateFn(state);
    documentStateSubject.next(state);
};

export const applicationState = {
    subscribe: (setState: any) => documentStateSubject.subscribe(setState),
    getUser: () => documentStateSubject.getValue().user,
    setUser: (user?: User) => {
        if (user) {
            if (user.roles === undefined || user.roles.length === 0) {
                user.roles = [];
            }

            // user.roles is expected to be an array and will cause exceptions if a string is used instead of an array of strings
            // this happens if a single group is given to the user such as only having 'GeneralAccess'
            else if (typeof (user.roles) === 'string') {
                user.roles = [user.roles];
            }

            setState(state => {
                state.user = user;
            });
        }
    },
    getProfile: () => {
        return documentStateSubject.getValue().profile;
    },
    setProfile: (profile?: UserProfile) => {
        setState(state => {
            if (profile?.cbtcTrained) {
                // Manually create a virtual cbtc role here
                const cbtcRole = {
                    id: 100, 
                    name: "cbtc",
                    permissions: ["web.cbtc.user.edit"],
                    isBackOfHouseRole: false
                }

                profile.roles.push(cbtcRole);
            }

            state.profile = profile;
            state.isBackOfHouseUser = profile?.roles.some(role => role.isBackOfHouseRole) ?? false;
        });
    },
    getSignedInState: () => documentStateSubject.getValue().signedInState,
    setSignedInState: (signedInState?: SignedInState) => {
        setState(state => { state.signedInState = signedInState; });
    },    
    setRailDocumentTypes: (documentTypes?: DocumentType[]) => {
        if (documentTypes) setState(state => { state.railDocumentTypes = documentTypes });
    },
    setSearchableRailDocumentTypes: (documentTypes?: DocumentType[]) => {
        let searchableTypes = documentTypes ?? [];
        const unsearchable = ["operatingnoticesandinstructions"];
        searchableTypes = searchableTypes.filter(t => !unsearchable.includes(t.key));
        if (searchableTypes) setState(state => { state.searchableRailDocumentTypes = searchableTypes });
    },
    setWritableRailDocumentTypes: (documentTypes?: DocumentType[]) => {
        if (documentTypes) setState(state => { state.writableRailDocumentTypes = documentTypes });
    },
    getGNHFormsFeatureToggle: () => documentStateSubject.getValue().gnhFormsFeatureToggle,
    setGNHFormsFeatureToggle: (gnhFeature: boolean) => {
        setState(state => { state.gnhFormsFeatureToggle = gnhFeature; });
    }
};

export const getUserExistingRulebookMode = () => {
    return localStorage.getItem('rulebookMode');
};

export const getUserRulebookModeFromLocation = (location: any, currentMode: string | null) => {
    if (location.pathname != '/' && !location.pathname.includes('search') || currentMode === null) {
        currentMode = location.pathname.includes('traditional') ? 'traditional' : location.pathname.includes('mbs') ? 'cbtc' : 'traditional';
        localStorage.setItem('rulebookMode', currentMode!);
    }
    return currentMode;
}